class SitePopup {
  static COOKIE_NAME = "site_popup_closed";
  static SCROLL_THRESHOLD = 0.7;

  /**
   * @param {string} selector - The popup element ID.
   */
  constructor(selector = "site-popup") {
    this.popup = document.getElementById(selector);
    if (!this.popup) return;

    this.hasSession = this.popup.dataset.session === "1";
    this.sessionHours = parseInt(this.popup.dataset.sessionHours, 10) || 0;
    if (!this.shouldShow()) return;

    this.closeBtn = this.popup.querySelector(".site-popup__close");
    this.backdrop = this.popup.querySelector(".site-popup__backdrop");
    this.hasShown = false;
    this.onScroll = this.handleScroll.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);

    window.addEventListener("scroll", this.onScroll, { passive: true });
  }

  /**
   * Checks whether the popup should be shown based on session settings.
   * If no session is enabled, always shows. Otherwise checks cookie expiry.
   * @returns {boolean}
   */
  shouldShow() {
    if (!this.hasSession) return true;

    const closedAt = SitePopup.getCookie(SitePopup.COOKIE_NAME);
    if (!closedAt) return true;

    const hoursSinceClosed =
      (Date.now() - parseInt(closedAt, 10)) / (1000 * 60 * 60);

    return hoursSinceClosed >= this.sessionHours;
  }

  /**
   * Scroll handler that opens the popup once the user passes the threshold.
   */
  handleScroll() {
    if (this.hasShown) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight;

    if (docHeight <= 0) return;

    if (scrollTop / docHeight >= SitePopup.SCROLL_THRESHOLD) {
      this.hasShown = true;
      this.open();
      window.removeEventListener("scroll", this.onScroll);
    }
  }

  /**
   * Displays the popup and binds close handlers.
   */
  open() {
    this.popup.classList.add("is-visible");
    this.popup.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    this.closeBtn.addEventListener("click", () => this.close());
    this.backdrop.addEventListener("click", () => this.close());
    document.addEventListener("keydown", this.onKeydown);
  }

  /**
   * Closes the popup with a fade-out animation and sets the dismissal cookie
   * only when session tracking is enabled.
   */
  close() {
    this.popup.classList.remove("is-visible");
    this.popup.classList.add("is-closing");
    this.popup.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";

    if (this.hasSession) {
      const cookieDays = this.sessionHours / 24;
      SitePopup.setCookie(
        SitePopup.COOKIE_NAME,
        Date.now().toString(),
        cookieDays,
      );
    }

    document.removeEventListener("keydown", this.onKeydown);

    this.popup.addEventListener(
      "animationend",
      () => this.popup.classList.remove("is-closing"),
      { once: true },
    );
  }

  /**
   * Keydown handler to close the popup on Escape.
   * @param {KeyboardEvent} e
   */
  handleKeydown(e) {
    if (e.key === "Escape") this.close();
  }

  /**
   * Sets a cookie with the given name, value, and expiry in days.
   * @param {string} name
   * @param {string} value
   * @param {number} days
   */
  static setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      ";expires=" +
      d.toUTCString() +
      ";path=/;SameSite=Lax";
  }

  /**
   * Retrieves the value of a cookie by name.
   * @param {string} name
   * @returns {string|null}
   */
  static getCookie(name) {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)"),
    );
    return match ? decodeURIComponent(match[2]) : null;
  }
}

/**
 * Initialises the SitePopup instance.
 */
function initPopup() {
  new SitePopup();
}

export default initPopup;
